import { type FirebaseApp, getApps, initializeApp } from 'firebase/app';
import { type Auth, connectAuthEmulator, getAuth } from 'firebase/auth';
import { type Firestore, connectFirestoreEmulator, getFirestore } from 'firebase/firestore';
import { type Functions, connectFunctionsEmulator, getFunctions } from 'firebase/functions';
import { type FirebaseStorage, connectStorageEmulator, getStorage } from 'firebase/storage';

type RequiredEnvKey =
  | 'VITE_FIREBASE_API_KEY'
  | 'VITE_FIREBASE_AUTH_DOMAIN'
  | 'VITE_FIREBASE_PROJECT_ID'
  | 'VITE_FIREBASE_APP_ID';

function readEnv(key: RequiredEnvKey): string {
  const value = import.meta.env[key];
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(
      `[firebase] Missing env var ${key}. Copy .env.example to .env.local and fill it in.`,
    );
  }
  return value;
}

function buildApp(): FirebaseApp {
  const existing = getApps()[0];
  if (existing) return existing;

  const storageBucket = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET;
  const messagingSenderId = import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID;

  return initializeApp({
    apiKey: readEnv('VITE_FIREBASE_API_KEY'),
    authDomain: readEnv('VITE_FIREBASE_AUTH_DOMAIN'),
    projectId: readEnv('VITE_FIREBASE_PROJECT_ID'),
    appId: readEnv('VITE_FIREBASE_APP_ID'),
    ...(storageBucket ? { storageBucket } : {}),
    ...(messagingSenderId ? { messagingSenderId } : {}),
  });
}

export const app: FirebaseApp = buildApp();
export const auth: Auth = getAuth(app);
export const firestore: Firestore = getFirestore(app);
// europe-west1 is mandated by ADR-016 (RGPD).
export const functions: Functions = getFunctions(app, 'europe-west1');
// Images de thème (fond / logo) — upload côté client, lecture publique
// (cf. storage.rules + TASK-035b). Le bucket vient de VITE_FIREBASE_STORAGE_BUCKET.
export const storage: FirebaseStorage = getStorage(app);

// Hot-module reload safety: connect once per page lifecycle.
declare global {
  // eslint-disable-next-line no-var
  var __MDP_EMULATORS_CONNECTED__: boolean | undefined;
}

if (
  import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true' &&
  !globalThis.__MDP_EMULATORS_CONNECTED__
) {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(firestore, '127.0.0.1', 8080);
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
  connectStorageEmulator(storage, '127.0.0.1', 9199);
  globalThis.__MDP_EMULATORS_CONNECTED__ = true;
}
